import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-control border border-border px-4 py-3 text-ui grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-1 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "border-destructive bg-destructive-surface text-destructive *:data-[slot=alert-description]:text-destructive",
        // Status notices wear the status roles (ADR 0029): the surface, its edge,
        // and the text are one contrast-tested pair per preset. A notice sits where
        // its action is; it is never a page banner.
        warning:
          "border-status-warning bg-status-warning-surface text-status-warning-foreground *:data-[slot=alert-description]:text-status-warning-foreground",
        info:
          "border-status-info bg-status-info-surface text-status-info-foreground *:data-[slot=alert-description]:text-status-info-foreground",
        success:
          "border-status-success bg-status-success-surface text-status-success-foreground *:data-[slot=alert-description]:text-status-success-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 min-w-0 font-semibold [overflow-wrap:anywhere]",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-ui [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
